import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { 
  getFocoScoreZone, 
  getFocoScoreLabel, 
  getFocoScoreInterpretation,
  FocoScoreZone 
} from '@/lib/foco-calculations';
import { Target, TrendingUp, AlertTriangle, Zap, Sparkles, ChevronDown, Info, PiggyBank, Wallet, Banknote, CheckCircle2, XCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ScoreComponentData {
  savingsRate?: number;
  expenseRatio?: number;
  activeGoalsWithProgress?: number;
  debtRatio?: number;
  cashFlow?: number;
  totalIncome?: number;
}

interface FocoScoreCardProps {
  score: number;
  className?: string;
  componentData?: ScoreComponentData;
  /** When false, hides the detailed score breakdown (free tier). */
  showDetailBreakdown?: boolean;
}

export function FocoScoreCard({
  score,
  className,
  componentData,
  showDetailBreakdown = true,
}: FocoScoreCardProps) {
  const [animatedScore, setAnimatedScore] = useState(0);
  const zone = getFocoScoreZone(score);
  const label = getFocoScoreLabel(zone);
  const interpretation = getFocoScoreInterpretation(score);

  // Animate score counting up
  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;
    const increment = score / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setAnimatedScore(score);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.floor(current));
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [score]);

  const zoneConfig: Record<FocoScoreZone, { 
    bgClass: string; 
    textClass: string;
    icon: typeof Target;
    color: string;
    gradientFrom: string;
    gradientTo: string;
  }> = {
    critical: { 
      bgClass: 'score-zone-critical', 
      textClass: 'text-destructive',
      icon: AlertTriangle,
      color: 'hsl(var(--destructive))',
      gradientFrom: 'hsl(0 84% 60%)',
      gradientTo: 'hsl(0 84% 40%)',
    },
    risk: { 
      bgClass: 'score-zone-risk', 
      textClass: 'text-warning',
      icon: Zap,
      color: 'hsl(var(--warning))',
      gradientFrom: 'hsl(38 92% 50%)',
      gradientTo: 'hsl(30 92% 40%)',
    },
    advance: { 
      bgClass: 'score-zone-advance', 
      textClass: 'text-success',
      icon: TrendingUp,
      color: 'hsl(var(--success))',
      gradientFrom: 'hsl(160 84% 39%)',
      gradientTo: 'hsl(160 84% 30%)',
    },
    liberty: { 
      bgClass: 'score-zone-liberty', 
      textClass: 'text-accent',
      icon: Target,
      color: 'hsl(var(--accent))',
      gradientFrom: 'hsl(204 64% 47%)',
      gradientTo: 'hsl(145 63% 49%)',
    },
  };

  const config = zoneConfig[zone];
  const Icon = config.icon;

  // Calculate stroke offset for circular progress
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

  return (
    <div className={cn(
      'rounded-2xl border p-4 sm:p-6 animate-scale-in relative overflow-hidden',
      config.bgClass,
      className
    )}>
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-5">
        <Sparkles className="w-full h-full" />
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 relative z-10">
        {/* Score Circle */}
        <div className="flex flex-row sm:flex-col items-center gap-4 sm:gap-0">
          <div className="relative h-28 w-28 sm:h-36 sm:w-36 flex-shrink-0 stat-ring">
            <svg className="h-28 w-28 sm:h-36 sm:w-36 -rotate-90 transform" viewBox="0 0 128 128">
              {/* Background circle */}
              <circle
                cx="64"
                cy="64"
                r={radius}
                stroke="currentColor"
                strokeWidth="10"
                fill="none"
                className="text-muted/20"
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id={`scoreGradient-${zone}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={config.gradientFrom} />
                  <stop offset="100%" stopColor={config.gradientTo} />
                </linearGradient>
              </defs>
              {/* Progress circle */}
              <circle
                cx="64"
                cy="64"
                r={radius}
                stroke={`url(#scoreGradient-${zone})`}
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-100 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-3xl sm:text-5xl font-bold tabular-nums', config.textClass)}>
                {animatedScore}
              </span>
              <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">de 100</span>
            </div>
          </div>
          <div className={cn(
            'flex sm:mt-4 items-center gap-2 px-3 py-1.5 rounded-full',
            zone === 'critical' && 'bg-destructive/10',
            zone === 'risk' && 'bg-warning/10',
            zone === 'advance' && 'bg-success/10',
            zone === 'liberty' && 'bg-accent/10',
          )}>
            <Icon className={cn('h-4 w-4', config.textClass)} />
            <span className={cn('text-xs sm:text-sm font-bold', config.textClass)}>
              {label}
            </span>
          </div>
        </div>

        {/* Interpretation */}
        <div className="flex-1 space-y-3 sm:space-y-4">
          <div className="flex items-center gap-2">
            <div className={cn(
              'h-1 w-8 rounded-full',
              zone === 'critical' && 'bg-destructive',
              zone === 'risk' && 'bg-warning',
              zone === 'advance' && 'bg-success',
              zone === 'liberty' && 'bg-accent',
            )} />
            <h3 className="text-base sm:text-lg font-bold">Tu FOCO Score</h3>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
            {interpretation}
          </p>
          
          {/* Quick tips based on zone */}
          {zone === 'critical' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/5 border border-destructive/20">
              <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-xs text-destructive">
                Prioriza revisar tus gastos y establecer un plan de acción inmediato.
              </p>
            </div>
          )}
          {zone === 'liberty' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/5 border border-accent/20">
              <Sparkles className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-xs text-accent">
                ¡Excelente trabajo! Mantén el ritmo y considera aumentar tus inversiones.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Desglose por componente */}
      {showDetailBreakdown && componentData && componentData.totalIncome && componentData.totalIncome > 0 && (
        <div className="relative z-10 mt-4 space-y-2">
          <p className="text-sm font-semibold">Desglose de tu puntaje</p>
          {(() => {
            const components = [
              {
                label: 'Tasa de ahorro ≥ 20%',
                icon: PiggyBank,
                points: 25,
                earned: (componentData.savingsRate ?? 0) >= 0.20,
                current: `${((componentData.savingsRate ?? 0) * 100).toFixed(1)}%`,
                tip: (componentData.savingsRate ?? 0) < 0.20
                  ? `Te falta ${(0.20 - (componentData.savingsRate ?? 0)) * 100 > 0 ? ((0.20 - (componentData.savingsRate ?? 0)) * 100).toFixed(1) : '0'}% para alcanzar la meta. Automatiza una transferencia pequeña cada quincena.`
                  : '¡Meta alcanzada! Considera diversificar entre fondo de emergencia e inversiones.',
              },
              {
                label: 'Gastos totales ≤ 80%',
                icon: Wallet,
                points: 25,
                earned: (componentData.expenseRatio ?? 1) <= 0.80,
                current: `${((componentData.expenseRatio ?? 0) * 100).toFixed(1)}%`,
                tip: (componentData.expenseRatio ?? 1) > 0.80
                  ? `Tus gastos representan más del 80%. Identifica los 3 gastos más altos y evalúa si puedes reducirlos o eliminarlos.`
                  : '¡Bien controlado! Mantén este equilibrio y redirige el excedente a metas.',
              },
              {
                label: '≥ 2 metas con avance',
                icon: Target,
                points: 20,
                earned: (componentData.activeGoalsWithProgress ?? 0) >= 2,
                current: `${componentData.activeGoalsWithProgress ?? 0} meta(s)`,
                tip: (componentData.activeGoalsWithProgress ?? 0) < 2
                  ? 'Crea al menos 2 metas con aportes regulares: una de corto plazo (emergencias) y una de largo plazo (retiro o inversión).'
                  : '¡Excelente! Tener metas activas da dirección a tu dinero.',
              },
              {
                label: 'Ratio de deuda ≤ 30%',
                icon: Banknote,
                points: 20,
                earned: (componentData.debtRatio ?? 0) <= 0.30,
                current: `${((componentData.debtRatio ?? 0) * 100).toFixed(1)}%`,
                tip: (componentData.debtRatio ?? 0) > 0.30
                  ? 'Tu deuda consume más del 30% de tus ingresos. Prioriza pagar la deuda con mayor interés o usa el método Bola de Nieve.'
                  : '¡Deuda bajo control! Si tienes deudas, considera abonar extra para liquidarlas más rápido.',
              },
              {
                label: 'Flujo de caja positivo',
                icon: TrendingUp,
                points: 10,
                earned: (componentData.cashFlow ?? 0) > 0,
                current: (componentData.cashFlow ?? 0) > 0 ? 'Superávit' : 'Déficit',
                tip: (componentData.cashFlow ?? 0) <= 0
                  ? 'Estás gastando más de lo que ingresa. Recorta gastos variables esta semana y pausa compras no esenciales.'
                  : '¡Cerraste con superávit! Asegúrate de asignar ese excedente a una meta específica.',
              },
            ];

            return (
              <div className="space-y-1.5">
                {components.map((comp) => {
                  const Icon = comp.icon;
                  return (
                    <Collapsible key={comp.label}>
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center gap-2 w-full px-3 py-2 rounded-lg bg-muted/40 hover:bg-muted/70 transition-colors group text-left">
                          {comp.earned ? (
                            <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-destructive/60 flex-shrink-0" />
                          )}
                          <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs font-medium flex-1">{comp.label}</span>
                          <span className={cn(
                            'text-xs font-bold min-w-[40px] text-right',
                            comp.earned ? 'text-success' : 'text-muted-foreground'
                          )}>
                            {comp.earned ? `+${comp.points}` : '0'} pts
                          </span>
                          <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mx-3 mt-1 mb-2 p-2.5 rounded-md bg-muted/20 border text-xs text-muted-foreground space-y-1">
                          <p>
                            <strong className="text-foreground">Actual:</strong> {comp.current}
                          </p>
                          <p>{comp.earned ? '✅' : '💡'} {comp.tip}</p>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Explicación del FOCO Score */}
      <Collapsible className="relative z-10 mt-4">
        <CollapsibleTrigger asChild>
          <button className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg bg-accent/10 hover:bg-accent/20 border border-accent/20 transition-colors group">
            <Info className="h-4 w-4 text-accent" />
            <span className="font-semibold text-sm text-accent">¿Cómo se calcula el FOCO Score?</span>
            <ChevronDown className="h-4 w-4 text-accent ml-auto transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className="rounded-lg bg-background/60 border p-4 space-y-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground text-sm">El FOCO Score evalúa tu salud financiera de 0 a 100 puntos:</p>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <span className="font-bold text-success min-w-[40px]">+25 pts</span>
                <span>Si tu <strong className="text-foreground">tasa de ahorro</strong> es ≥ 20% de tus ingresos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success min-w-[40px]">+25 pts</span>
                <span>Si tus <strong className="text-foreground">gastos totales</strong> (fijos + variables + deudas) son ≤ 80% del ingreso</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success min-w-[40px]">+20 pts</span>
                <span>Si tienes <strong className="text-foreground">≥ 2 metas activas</strong> con al menos 10% de avance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success min-w-[40px]">+20 pts</span>
                <span>Si tu <strong className="text-foreground">ratio de deuda</strong> es ≤ 30% del ingreso</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-success min-w-[40px]">+10 pts</span>
                <span>Si tu <strong className="text-foreground">flujo de caja real</strong> es positivo (superávit)</span>
              </li>
            </ul>
            <div className="pt-2 border-t space-y-1.5">
              <p className="font-medium text-foreground text-sm">Zonas de interpretación:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-destructive" />
                  <span><strong className="text-destructive">0–39:</strong> Zona Crítica</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-warning" />
                  <span><strong className="text-warning">40–59:</strong> Zona de Riesgo</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-success" />
                  <span><strong className="text-foreground">60–79:</strong> Zona de Avance</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  <span><strong className="text-accent">80–100:</strong> Zona de Libertad</span>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
